import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import Swal from "sweetalert2";

import PageTitle from "../../../layouts/PageTitle";
import { appApi, useGetEquipmentListQuery } from "../../../../store/appApi";
import { logServiceApi } from "../../../../services/equipementService";

const SERVICE_TYPES = [
  "Kilometer",
  "Time-Based",
  "Hour-Based",
  "Usage-Based",
  "Fuel Consumption",
];

const STATUS_BADGE = {
  Active: "success",
  Inactive: "secondary",
  "Under Service": "warning",
  Breakdown: "danger",
  Relocating: "info",
};

const metricLabel = (serviceType) => {
  switch (serviceType) {
    case "Kilometer":      return "Current Mileage (km)";
    case "Hour-Based":
    case "Time-Based":     return "Total Hours";
    case "Usage-Based":    return "Usage Units";
    case "Fuel Consumption": return "Fuel (Litres)";
    default:               return "Metric Value";
  }
};

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

const FORM_EMPTY = {
  equipmentId: "",
  date: new Date(),
  serviceType: "",
  metricValue: "",
  serviceCost: "",
  maintenanceCost: "",
  serviceProvider: "",
  performedBy: "",
  notes: "",
};

const ServiceLogs = () => {
  const dispatch = useDispatch();
  const { data, isLoading, isError } = useGetEquipmentListQuery();
  const items = data?.items || [];

  // ── Add-form state ────────────────────────────────────────────────────────
  const [form, setForm] = useState(FORM_EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.equipmentId) {
      Swal.fire("Validation", "Please select an equipment.", "warning");
      return;
    }
    if (!form.serviceType) {
      Swal.fire("Validation", "Please select a service type.", "warning");
      return;
    }
    setSubmitting(true);
    try {
      await logServiceApi(form.equipmentId, {
        date: form.date,
        serviceType: form.serviceType,
        metricValue: form.metricValue !== "" ? Number(form.metricValue) : undefined,
        serviceCost: Number(form.serviceCost) || 0,
        maintenanceCost: Number(form.maintenanceCost) || 0,
        serviceProvider: form.serviceProvider,
        performedBy: form.performedBy,
        notes: form.notes,
      });
      dispatch(appApi.util.invalidateTags(["Equipment"]));
      Swal.fire({ icon: "success", title: "Service record added.", timer: 1500, showConfirmButton: false });
      setForm(FORM_EMPTY);
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || "Failed to save.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Flatten all service logs ───────────────────────────────────────────────
  const allLogs = items
    .flatMap((eq) =>
      (eq.serviceHistory || []).map((s) => ({
        ...s,
        equipmentId: eq._id,
        equipmentName: eq.eqpName,
        equipmentNumber: eq.eqpNumber,
        projectName: eq.projectName,
        eqpStatus: eq.eqpStatus,
      }))
    )
    .sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt));

  return (
    <>
      <PageTitle activeMenu="Service Logs" motherMenu="Daily Progress / Equipment" />

      {/* ── Add Service Log Form ─────────────────────────────────────────── */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="card-title mb-0">
            <i className="la la-plus-circle me-2 text-primary" />
            Log New Service
          </h5>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Equipment selector */}
              <div className="col-md-4 form-group">
                <label className="font-weight-bold">Equipment <span className="text-danger">*</span></label>
                <select
                  className="form-control"
                  name="equipmentId"
                  value={form.equipmentId}
                  onChange={handleChange}
                >
                  <option value="">-- Select Equipment --</option>
                  {items.map((eq) => (
                    <option key={eq._id} value={eq._id}>
                      {eq.eqpName}{eq.eqpNumber ? ` (${eq.eqpNumber})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date */}
              <div className="col-md-3 form-group">
                <label className="font-weight-bold">Service Date</label>
                <DatePicker
                  selected={form.date}
                  onChange={(d) => setForm((prev) => ({ ...prev, date: d }))}
                  className="form-control"
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Select date"
                />
              </div>

              {/* Service Type */}
              <div className="col-md-3 form-group">
                <label className="font-weight-bold">Service Type <span className="text-danger">*</span></label>
                <select
                  className="form-control"
                  name="serviceType"
                  value={form.serviceType}
                  onChange={handleChange}
                >
                  <option value="">-- Select Type --</option>
                  {SERVICE_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              {/* Metric Value (conditional) */}
              {form.serviceType && (
                <div className="col-md-2 form-group">
                  <label className="font-weight-bold">{metricLabel(form.serviceType)}</label>
                  <input
                    type="number"
                    className="form-control"
                    name="metricValue"
                    value={form.metricValue}
                    onChange={handleChange}
                    min="0"
                    step="any"
                    placeholder="0"
                  />
                </div>
              )}

              {/* Service Cost */}
              <div className="col-md-3 form-group">
                <label className="font-weight-bold">Service Cost (₹)</label>
                <input
                  type="number"
                  className="form-control"
                  name="serviceCost"
                  value={form.serviceCost}
                  onChange={handleChange}
                  min="0"
                  step="any"
                  placeholder="0"
                />
              </div>

              {/* Maintenance Cost */}
              <div className="col-md-3 form-group">
                <label className="font-weight-bold">Maintenance Cost (₹)</label>
                <input
                  type="number"
                  className="form-control"
                  name="maintenanceCost"
                  value={form.maintenanceCost}
                  onChange={handleChange}
                  min="0"
                  step="any"
                  placeholder="0"
                />
              </div>

              {/* Service Provider */}
              <div className="col-md-3 form-group">
                <label className="font-weight-bold">Service Provider</label>
                <input
                  type="text"
                  className="form-control"
                  name="serviceProvider"
                  value={form.serviceProvider}
                  onChange={handleChange}
                  placeholder="Provider name"
                />
              </div>

              {/* Performed By */}
              <div className="col-md-3 form-group">
                <label className="font-weight-bold">Performed By</label>
                <input
                  type="text"
                  className="form-control"
                  name="performedBy"
                  value={form.performedBy}
                  onChange={handleChange}
                  placeholder="Technician name"
                />
              </div>

              {/* Notes */}
              <div className="col-md-12 form-group">
                <label className="font-weight-bold">Notes</label>
                <textarea
                  className="form-control"
                  rows={2}
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="Additional notes…"
                />
              </div>
            </div>

            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" role="status" />
                  Saving…
                </>
              ) : (
                "Log Service"
              )}
            </button>
          </form>
        </div>
      </div>

      {/* ── All Service Logs Table ─────────────────────────────────────────── */}
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h4 className="card-title mb-0">
            <i className="la la-wrench me-2 text-primary" />
            All Service Logs
          </h4>
          {!isLoading && (
            <span className="badge badge-primary">{allLogs.length} records</span>
          )}
        </div>

        <div className="card-body">
          {isLoading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status" />
            </div>
          )}

          {isError && (
            <div className="alert alert-danger">Failed to load service logs.</div>
          )}

          {!isLoading && !isError && allLogs.length === 0 && (
            <div className="text-center text-muted py-5">
              No service records yet. Use the form above to add one.
            </div>
          )}

          {!isLoading && allLogs.length > 0 && (
            <div className="table-responsive">
              <table className="table table-bordered table-hover verticle-middle">
                <thead className="thead-dark">
                  <tr>
                    <th>#</th>
                    <th>Service Date</th>
                    <th>Equipment</th>
                    <th>Project</th>
                    <th>Status</th>
                    <th>Service Type</th>
                    <th>Metric</th>
                    <th>Service Cost</th>
                    <th>Maint. Cost</th>
                    <th>Provider</th>
                    <th>Performed By</th>
                    <th>Notes</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {allLogs.map((log, idx) => (
                    <tr key={`${log.equipmentId}-${log._id || idx}`}>
                      <td className="text-muted small">{idx + 1}</td>
                      <td className="small">
                        {log.date ? moment(log.date).format("DD MMM YYYY") : "—"}
                      </td>
                      <td>
                        <strong>{log.equipmentName}</strong>
                        {log.equipmentNumber && (
                          <div className="small text-muted">{log.equipmentNumber}</div>
                        )}
                      </td>
                      <td className="small">{log.projectName || "—"}</td>
                      <td>
                        <span
                          className={`badge light badge-${
                            STATUS_BADGE[log.eqpStatus] || "secondary"
                          }`}
                        >
                          {log.eqpStatus || "—"}
                        </span>
                      </td>
                      <td>
                        <span className="badge badge-primary light">
                          {log.serviceType || "—"}
                        </span>
                      </td>
                      <td className="small">
                        {log.metricValue != null ? fmt(log.metricValue) : "—"}
                      </td>
                      <td className="small text-success fw-semibold">
                        {log.serviceCost != null ? `₹${fmt(log.serviceCost)}` : "—"}
                      </td>
                      <td className="small text-warning fw-semibold">
                        {log.maintenanceCost != null ? `₹${fmt(log.maintenanceCost)}` : "—"}
                      </td>
                      <td className="small">{log.serviceProvider || "—"}</td>
                      <td className="small">{log.performedBy || "—"}</td>
                      <td className="small text-muted" style={{ maxWidth: 160 }}>
                        {log.notes || "—"}
                      </td>
                      <td>
                        <Link
                          to={`/progress/equipement/${log.equipmentId}/detail`}
                          className="btn btn-xs btn-outline-primary"
                          title="View equipment detail"
                        >
                          <i className="fa fa-eye" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ServiceLogs;
